import React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import styled, { css } from "styled-components";
import Button from "components/foundation/Button";

const ModalRoot = Dialog.Root;
const ModalTrigger = Dialog.Trigger;

interface ModalLayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  overflowVisible?: boolean;
}

const ModalLayout = ({
  children,
  overflowVisible,
  ...rest
}: ModalLayoutProps) => {
  return (
    <Dialog.Portal>
      <DialogOverlay />
      <DialogLayout {...rest}>{children}</DialogLayout>
    </Dialog.Portal>
  );
};

const DialogOverlay = styled(Dialog.Overlay)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);

  z-index: ${({ theme }) => theme.zIndex.modal};
`;

const DialogLayout = styled(Dialog.Content)`
  padding: 3.6rem 4rem 4rem 4rem;
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.color.background.white};
  max-height: calc(100% - 140px);
  min-width: 38rem;

  border-radius: 1rem;
  box-shadow: 0px 10px 20px 0px rgba(0, 0, 0, 0.1);

  display: flex;
  flex-direction: column;
  overflow: auto;

  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  z-index: ${({ theme }) => theme.zIndex.modal};

  ${({ theme }) => css`
    ${theme.media.small} {
      padding: 3rem 3rem 3.4rem 3rem;
      min-width: 30rem;
    }
  `}
`;

const ModalTitle = styled(Dialog.Title)`
  ${({ theme }) => css`
    ${theme.typography.t1.semibold};
    margin-bottom: 2rem;
    word-break: keep-all;

    ${theme.media.small} {
      ${theme.typography.sub1.semibold};
    }
  `};
`;

const ModalSubTitle = styled(Dialog.Title)`
  ${({ theme }) => theme.typography.body1.regular};
`;

const ModalContent = styled(Dialog.Description)<{ overflowVisible?: boolean }>`
  ${({ theme }) => theme.typography.body1.regular};
  color: ${({ theme }) => theme.color.text.secondary};
  margin-bottom: 3rem;
  box-sizing: border-box;
  overflow: ${({ overflowVisible }) => (overflowVisible ? "visible" : "auto")};
  scrollbar-width: none;
  flex-grow: 1;

  ${({ theme }) => css`
    ${theme.media.small} {
      ${theme.typography.body3.regular}
      letter-spacing: -0.042rem;
    }
  `}
`;

export interface ModalBodyProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  title?: React.ReactNode;
  className?: string;
  confirm?: boolean;
  children?: React.ReactNode;
  buttonContainerStyle?: React.CSSProperties;
  onCancel?: ((e: React.MouseEvent<HTMLButtonElement>) => void) | boolean;
  onConfirm?: ((e: React.MouseEvent<HTMLButtonElement>) => void) | boolean;
  confirmValue?: string;
  cancelValue?: string;
  overflowVisible?: boolean;
  // 추가 (옵셔널, 기본값은 'default')
  confirmType?: "default" | "danger";
}

const ModalBody = ({
  title,
  confirm,
  children,
  buttonContainerStyle,
  onCancel,
  cancelValue,
  onConfirm,
  confirmValue,
  overflowVisible,
  confirmType = "default",
  ...rest
}: ModalBodyProps) => {
  return (
    <ModalLayout {...rest}>
      {title && <ModalTitle>{title}</ModalTitle>}
      {children && (
        <ModalContent overflowVisible={overflowVisible}>
          {children}
        </ModalContent>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "1rem",
          ...buttonContainerStyle,
        }}
      >
        {onCancel && (
          <Dialog.Close asChild>
            <Button
              variant="tertiary"
              size="large"
              onClick={typeof onCancel === "boolean" ? undefined : onCancel}
            >
              {cancelValue || "취소"}
            </Button>
          </Dialog.Close>
        )}
        {(confirm || onConfirm) && (
          <Dialog.Close asChild>
            <Button
              variant="primary"
              size="large"
              style={
                confirmType === "danger"
                  ? {
                      backgroundColor: "white",
                      color: "red",
                      border: "1px solid red",
                    }
                  : undefined
              }
              onClick={typeof onConfirm === "boolean" ? undefined : onConfirm}
            >
              {confirmValue || "확인"}
            </Button>
          </Dialog.Close>
        )}
      </div>
    </ModalLayout>
  );
};

/**
 *  @responsive 반응형 컴포넌트
 *
 * */
const Modal = {
  Layout: ModalLayout,
  Title: ModalTitle,
  SubTitle: ModalSubTitle,
  Content: ModalContent,
  Close: Dialog.Close,

  // 일반적으로 사용할 Modal 컴포넌트
  Root: ModalRoot,
  Trigger: ModalTrigger,
  Body: ModalBody,
};

export default Modal;
